// @flow
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import * as DmsCommon from './../supports/dms-common';
import * as DmsService from './../supports/dms-service';
import { GlobalEvent, GlobalState } from './GlobalContext'
import MsgDialog from './MsgDialog';
import DocShareAddDialog from './DocShareAddDialog';
import PropTypes from 'prop-types';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConfirmDialog from './ConfirmDialog';
import dayjs from "dayjs"

import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';

export default function DocShareTab(props) {
  const { sysID, docDetail, afterUpdate } = props;

  // Get Global state and dispatcher
  const [gState, gStateDispatch] = React.useContext(GlobalState);

  const [gEvent, gEventDispatch] = React.useContext(GlobalEvent);

  const shares = docDetail?.ShareDocHists || [];

  const [showLoading, setShowLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const onCatchPromiseError = (e) => {
    gEventDispatch({
      showErr: true,
      err: e,
    });

  }


  const gridData = shares.map(item => {
    return {
      ...item,
      ShareDateJs: item.CreateDate ? dayjs(item.CreateDate) : null,
      ShareDateDisplay: item.CreateDate ? dayjs(item.CreateDate).format(DmsCommon.SHORT_DATETIME_FORMAT) : "",
      DownloadDateJs: item.DownloadDate ? dayjs(item.DownloadDate) : null,
      DownloadDateDisplay: item.DownloadDate ? dayjs(item.DownloadDate).format(DmsCommon.SHORT_DATETIME_FORMAT) : "",
      ExpiryDateJs: item.ExpiryDate ? dayjs(item.ExpiryDate) : null,
      ExpiryDateDisplay: item.ExpiryDate ? dayjs(item.ExpiryDate).format(DmsCommon.SHORT_DATE_FORMAT) : "",
      Status: item.Disabled ? "Disabled" : ""

    }
  })

  // console.log("gridData", gridData);

  const [selectedCmt, setSelectedCmt] = useState(null);

  const onSelectionChanged = (params) => {
    let nodes = params.api.getSelectedNodes();

    let selRecs = nodes.map(item => {
      return item.data
    });


    if (selRecs.length > 0) {
      setSelectedCmt(selRecs[0])
    } else {
      setSelectedCmt(null)
    }

  }


  const [openAddShareDialog, setOpenAddShareDialog] = useState(false);

  const handleShareClick = () => {

    if (docDetail.Permission < DmsCommon.AccessRightEnum.arReadDn) {
      setErrMsg("Need download or above permission for document sharing!");
      setShowError(true);
      return
    }

    setOpenAddShareDialog(true);
  }

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const handleDisableClick = () => {

    if (selectedCmt) {
      setOpenConfirmDialog(true);
    }

  }

  const handleConfirmDeleteClick = () => {
    if (selectedCmt) {
      // console.log("selectedCmt", selectedCmt);

      setShowLoading(true);
      DmsService.shareLinkDelPromise(gState.sessionID, sysID, selectedCmt.DocLinkUID)
        .then(() => {
          setShowLoading(false);

          afterUpdate && afterUpdate();
          setSelectedCmt(null)

        })
        .catch(e => {
          setShowLoading(false);
          onCatchPromiseError(e);
        })
    }
  }

  const handleAfterUpdate = () => {
    setOpenAddShareDialog(false);
    afterUpdate && afterUpdate();
    setSelectedCmt(null)
  }




  const comparatorShareDate = (a, b, nodeA, nodeB) => {

    const [aDate, bDate] = [nodeA.data.ShareDateJs, nodeB.data.ShareDateJs];
    if (aDate.isBefore(bDate)) {
      return -1;
    } else if (aDate.isAfter(bDate)) {
      return 1;
    } else {
      return 0;
    }
  }

  const comparatorDownloadDate = (a, b, nodeA, nodeB) => {

    const [aDate, bDate] = [nodeA.data.DownloadDateJs, nodeB.data.DownloadDateJs];
    if (aDate.isBefore(bDate)) {
      return -1;
    } else if (aDate.isAfter(bDate)) {
      return 1;
    } else {
      return 0;
    }
  }

  const comparatorExpiryDate = (a, b, nodeA, nodeB) => {

    const [aDate, bDate] = [nodeA.data.ExpiryDateJs, nodeB.data.ExpiryDateJs];
    if (aDate.isBefore(bDate)) {
      return -1;
    } else if (aDate.isAfter(bDate)) {
      return 1;
    } else {
      return 0;
    }
  }



  const cellStyle = { paddingLeft: '10px', paddingRight: '10px', fontSize: '0.8rem' };


  const compareStrInsensitive = (aStrA, aStrB) => {
    return aStrA.localeCompare(aStrB, undefined, { sensitivity: "base" })
  }

  const comparatorDept = (a, b, nodeA, nodeB) => {
    return compareStrInsensitive(nodeA.data.DeptName, nodeB.data.DeptName)
  }


  const columnDefs = [
    { field: 'ShareDateDisplay', headerName: "Shared Date", cellStyle: cellStyle, minWidth: 150, comparator: comparatorShareDate },
    { field: 'SendToEmail', headerName: "To", cellStyle: cellStyle, width: 150, comparator: comparatorDept },
    { field: 'DownloadDateDisplay', headerName: "Download Date", cellStyle: cellStyle, minWidth: 150, flex: "1", comparator: comparatorDownloadDate },
    { field: 'ExpiryDateDisplay', headerName: "Expiration Date", cellStyle: cellStyle, minWidth: 150, flex: "1", comparator: comparatorExpiryDate },
    { field: 'Status', headerName: "Status", cellStyle: cellStyle },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: "70vh" }} >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MsgDialog
        open={showError}
        title="Error"
        msgText={errMsg}
        onClose={() => setShowError(false)}
      />
      <ConfirmDialog
        open={openConfirmDialog}
        text="Disable the selected document share link?"
        confirmBtnCaption='Disable'
        onCancelClick={() => {
          setOpenConfirmDialog(false);
        }}
        onConfirmClick={() => {
          setOpenConfirmDialog(false);
          handleConfirmDeleteClick();
        }}
      />


      <div className="ag-theme-material" style={{ width: '100%', height: '100%', bgcolor: 'yellow' }}>
        <Box sx={{ py: 1, px: 1 }} >

          <Button startIcon={<SendIcon />} onClick={handleShareClick} >
            Share
          </Button>
          <DocShareAddDialog
            sysID={sysID}
            open={openAddShareDialog}
            onClose={() => setOpenAddShareDialog(false)}
            docDetail={docDetail}
            afterUpdate={handleAfterUpdate}
          />

          <Button disabled={!selectedCmt} startIcon={<DoDisturbIcon />} onClick={handleDisableClick} >
            Disable
          </Button>

        </Box>

        <AgGridReact

          rowData={gridData}

          defaultColDef={{
            width: 100,
            resizable: true,
            sortable: true,
            suppressMovable: true,
          }}
          columnDefs={columnDefs}
          headerHeight={40}

          suppressNoRowsOverlay

          rowSelection="single"
          onSelectionChanged={onSelectionChanged}
        >

        </AgGridReact>

      </div>

    </Box>


  );
}

DocShareTab.propTypes = {
  sysID: PropTypes.string,
  docDetail: PropTypes.object,
  afterUpdate: PropTypes.func,

};
